/**
 * Editing a community's own list of channels.
 *
 * TAKING OVER FROM A FEED IS THE INTERESTING CASE. A community can draw its
 * roster from somebody else's site, and while it does, its own
 * `twitch_logins` is empty — so detaching without copying first empties the
 * community. The server deliberately does not copy for you (a borrowed list
 * quietly becoming an owned one is not a thing to do behind somebody's back),
 * which makes "copy and detach" a single intention that must be sent as a
 * single patch. Two calls would leave a window where the community is empty,
 * and a failure between them would leave it that way.
 */

/** A login, as somebody types it: trimmed, lowercased, `@` and URLs forgiven. */
export function cleanLogin(raw: string): string | null {
  const text = raw.trim().toLowerCase()
    // People paste the address of the person they mean.
    .replace(/^https?:\/\/(www\.)?twitch\.tv\//, '')
    .replace(/^@/, '')
    .replace(/[/?#].*$/, '');
  /*
   * The SAME rule the contract enforces, restated because `apps/` reads the
   * wire schema rather than importing a validator — and a client that guessed
   * a looser rule would send names the server refuses, with the refusal
   * arriving as a failed save instead of a message beside the box.
   * `TwitchLogin` in @tomo/contracts is the original.
   */
  return /^[a-z0-9_]{3,25}$/.test(text) ? text : null;
}

/**
 * Add a login to the list, or say why not.
 *
 * Returns the unchanged list for a duplicate rather than erroring: pressing
 * add on somebody already there has got what it wanted.
 */
export function addLogin(
  current: readonly string[],
  raw: string,
  max: number,
): { logins: string[]; error: string | null } {
  const login = cleanLogin(raw);
  if (login === null) return { logins: [...current], error: 'That is not a Twitch name.' };
  if (current.includes(login)) return { logins: [...current], error: null };
  if (current.length >= max) {
    return { logins: [...current], error: `A community holds at most ${String(max)} channels.` };
  }
  return { logins: [...current, login], error: null };
}

/** What a pasted list did, so the editor can say it in one line. */
export interface BulkAdd {
  readonly logins: string[];
  readonly added: number;
  readonly already: number;
  /** What was pasted that is not a Twitch name, as pasted. */
  readonly invalid: string[];
  /** Names that did not fit under the cap, in the order they were pasted. */
  readonly overflow: string[];
}

/**
 * Add everything in a pasted list: one name per line, or separated by
 * commas or spaces — however a spreadsheet column or a chat message arrives.
 *
 * Adding four hundred people one at a time is not an editor, it is a chore
 * nobody finishes. Everything that CAN be added is, and what cannot is
 * reported rather than failing the whole paste: one typo in a list of four
 * hundred should cost one name, not four hundred.
 */
export function addLogins(current: readonly string[], raw: string, max: number): BulkAdd {
  const logins = [...current];
  const held = new Set(current);
  let added = 0;
  let already = 0;
  const invalid: string[] = [];
  const overflow: string[] = [];
  for (const token of raw.split(/[\s,;]+/)) {
    if (token.length === 0) continue;
    const login = cleanLogin(token);
    if (login === null) { invalid.push(token); continue; }
    if (held.has(login)) { already += 1; continue; }
    if (logins.length >= max) { overflow.push(login); continue; }
    held.add(login);
    logins.push(login);
    added += 1;
  }
  return { logins, added, already, invalid, overflow };
}

/** A pasted list's result, in a sentence. Empty when nothing needs saying. */
export function describeBulk(result: BulkAdd, max: number): string {
  const parts: string[] = [];
  if (result.added > 0) parts.push(`Added ${String(result.added)}.`);
  if (result.already > 0) parts.push(`${String(result.already)} already here.`);
  if (result.invalid.length > 0) {
    const shown = result.invalid.slice(0, 5).join(', ');
    const more = result.invalid.length > 5 ? ` and ${String(result.invalid.length - 5)} more` : '';
    parts.push(`Not Twitch names: ${shown}${more}.`);
  }
  if (result.overflow.length > 0) {
    parts.push(`${String(result.overflow.length)} did not fit — a community holds at most ${String(max)}.`);
  }
  return parts.join(' ');
}

/** Whether a paste is a list rather than one name. */
export function looksLikeList(raw: string): boolean {
  return raw.trim().split(/[\s,;]+/).length > 1;
}

export function removeLogin(current: readonly string[], login: string): string[] {
  return current.filter((l) => l !== login);
}

/** Whether anything is actually different, so Save can say so. */
export function hasChanges(saved: readonly string[], draft: readonly string[]): boolean {
  if (saved.length !== draft.length) return true;
  return saved.some((login, i) => draft[i] !== login);
}
